import { CONVERSACION, DOCUMENTO, TONE_CLASS } from './statusMaps'
import { expedienteCompleto, expedienteObservado, requiereRevisionCritica } from '../atencion'

/** Lo que el backend resuelve de §9.30. `null` cuando la conversación todavía
 *  no tiene expediente. */
export type Expediente = { estado: string; label: string } | null

/**
 * Pill de estado — fuente única para conversaciones y documentos.
 * Los mapas y `statusTone` viven en `statusMaps.ts`.
 *
 * POR QUÉ «REQUIERE ASESOR» NO ALCANZA
 * ------------------------------------
 * `status = HUMAN_REQUIRED` cubre dos situaciones que el PDD separa en §9.30 y
 * que piden acciones opuestas:
 *
 *   DERIVADO_A_EJECUTIVO                 alguien tiene que atenderlo YA; el
 *                                        cliente está esperando en el chat
 *   DOCUMENTACION_RECIBIDA_CON_ALERTAS   ya se atendió y cerró; falta que una
 *                                        persona VALIDE las alertas
 *
 * Las dos se pintaban «Requiere asesor» en rojo, así que un caso cerrado que
 * solo necesita validación gritaba igual que uno con un cliente esperando —y
 * el supervisor no tenía cómo priorizar. Reportado por el COE el 14-set-2026
 * sobre un expediente completo con un embargo detectado.
 *
 * La etiqueta NO se redacta acá: viene del backend, que la saca de
 * `teleagent.expediente.ETIQUETAS`, la transcripción literal de §9.30.
 */
export default function StatusPill({ status, kind = 'conversation', expediente = null, className = '' }: {
  status: string
  kind?: 'conversation' | 'document'
  expediente?: Expediente
  className?: string
}) {
  const mapa = kind === 'document' ? DOCUMENTO : CONVERSACION
  const entry = mapa[status]
  // Un estado que no está en el mapa se muestra tal cual en gris, no se
  // esconde: si el backend agrega uno nuevo, la UI lo hace visible en vez de
  // dejar la celda vacía.
  let label = entry?.label ?? (status || '—')
  let tone = entry?.tone ?? 'gray'

  // DEF-039: un expediente que se completó con un bloqueo, un embargo o una
  // restricción es lo que no puede avanzar sin que una persona lo mire. Va en
  // rojo, antes que el ámbar de «con alertas», y con la etiqueta del backend
  // (`teleagent.expediente.ETIQUETAS`): «Requiere revisión: alerta crítica».
  if (kind === 'conversation' && expediente
      && requiereRevisionCritica({ status, expediente })) {
    label = expediente.label
    tone = 'red'
  } else if (kind === 'conversation' && status === 'HUMAN_REQUIRED'
      && expediente && expediente.estado !== 'DERIVADO_A_EJECUTIVO') {
    // El rojo se reserva para lo que necesita a alguien AHORA. Un expediente
    // que cerró con alertas es trabajo pendiente, no una urgencia: ámbar.
    label = expediente.label
    tone = 'amber'
  } else if (kind === 'conversation' && expediente
      && expedienteObservado({ status, expediente })) {
    // DEF-051: se espera que el cliente corrija o reenvíe algo —lo observó el
    // bot, o el asesor lo rechazó o lo pidió de nuevo—. Dice eso, con la
    // etiqueta de §9.30, y no «En revisión» o «Activa», que no lo cuentan: es
    // lo mismo que leen la Ficha y la tarjeta del documento.
    label = expediente.label
    tone = 'amber'
  } else if (kind === 'conversation' && expedienteCompleto(expediente)) {
    // El expediente cerró sin nada pendiente. El `status` dice IN_REVIEW, que
    // en pantalla se leía «En revisión» como si alguien lo estuviera
    // revisando. «Completo» es lo que el contrato con Software llama
    // COMPLETED; el detalle literal de §9.30 va en el tooltip.
    label = 'Completo'
    tone = 'green'
  }

  return (
    <span
      className={`inline-flex items-center rounded-chip px-2 py-0.5 text-pill font-bold ${TONE_CLASS[tone]} ${className}`}
      title={expediente ? expediente.label : undefined}
    >
      {label}
    </span>
  )
}
